import {
  Badge,
  EmptyState,
  EnvBadge,
  Icon,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  useToast,
} from '@/components';
import { useReleaseProducts, useUpdateManifest } from '@/features/release-config/hooks';
import { useReleases } from '@/features/releases/hooks';
import { apiCall } from '@/lib/api';
import { cn } from '@/lib/cn';
import { A, useSearchParams } from '@solidjs/router';
import { createQuery } from '@tanstack/solid-query';
import {
  RELEASE_ENVIRONMENTS,
  type ReleaseChannelInfo,
  type ReleaseEnvironment,
  type ReleaseRow,
  listRequiredArtifacts,
} from '@uchat-team-management/shared';
import { For, type JSX, Show, createMemo, createSignal, onCleanup, onMount } from 'solid-js';

/**
 * Per-environment published-update view (UCH-152). Environments are complete
 * universes: each has its own package id, artifacts, and latest pointer.
 */
export default function DesktopUpdatesIndex() {
  const products = useReleaseProducts();
  // UCH-447: the product filter lives in the URL so a refresh keeps it.
  const [params, setParams] = useSearchParams<{ product?: string }>();

  const desktopProducts = createMemo(() =>
    (products.data?.data ?? []).filter((p) => p.adapter === 'desktop_app'),
  );

  const effectiveProductId = createMemo(() => {
    if (params.product) return params.product;
    return desktopProducts()[0]?.id ?? '';
  });

  const product = createMemo(() => desktopProducts().find((p) => p.id === effectiveProductId()));

  const published = useReleases(() => ({
    productId: effectiveProductId() || undefined,
    status: 'published',
  }));

  return (
    <section>
      <PageHeader
        title="Desktop Updates"
        sub="Latest published update per environment. Artifacts are never promoted between environments."
        actions={
          <Select
            value={effectiveProductId()}
            onChange={(v) => setParams({ product: v || undefined })}
            options={desktopProducts().map((p) => ({ value: p.id, label: p.name }))}
            class="min-w-48"
          />
        }
      />

      <div class="mx-auto max-w-6xl space-y-4 p-6">
        <Show when={!products.isPending} fallback={<Skeleton class="h-40" />}>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <For each={[...RELEASE_ENVIRONMENTS]}>
              {(environment) => (
                <EnvCard
                  environment={environment}
                  productId={effectiveProductId()}
                  channel={product()?.channels.find((c) => c.environment === environment)}
                  published={published.data?.data ?? []}
                />
              )}
            </For>
          </div>
        </Show>
      </div>
    </section>
  );
}

function EnvCard(props: {
  environment: ReleaseEnvironment;
  productId: string;
  channel: ReleaseChannelInfo | undefined;
  published: ReleaseRow[];
}) {
  const toast = useToast();
  const [open, setOpen] = createSignal(false);
  // Enabled on first open, then left on — solid-query caches, so reopening is free.
  const [requested, setRequested] = createSignal(false);
  const manifest = useUpdateManifest(() => ({
    productId: props.productId,
    environment: props.environment,
    enabled: requested(),
  }));

  const info = () => {
    const ch = props.channel;
    if (!ch?.versionFloor) return null;
    const release = props.published.find(
      (r) => r.environment === props.environment && r.version === ch.versionFloor,
    );
    return { floor: ch.versionFloor, release };
  };

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    toast.success('Copied');
  };

  return (
    <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
      <div class="mb-1 flex items-center justify-between">
        <EnvBadge env={props.environment} />
        <Show when={props.channel?.tauriPackageId}>
          <code class="text-xs text-neutral-500">{props.channel?.tauriPackageId}</code>
        </Show>
      </div>
      <Show when={info()} fallback={<EmptyState title="Nothing published yet" class="py-4" />}>
        {(i) => (
          <div class="flex items-center gap-2">
            <Badge tone="success">{i().floor}</Badge>
            <Show when={i().release}>
              {(r) => (
                <A
                  href={`/release/versions/${r().id}`}
                  class="text-sm text-brand-600 hover:underline dark:text-brand-400"
                >
                  build #{r().buildNumber}
                </A>
              )}
            </Show>
          </div>
        )}
      </Show>
      <Show when={props.channel?.updateManifestUrl}>
        {(url) => (
          <div class="mt-3 border-t border-neutral-200 pt-2 dark:border-neutral-800">
            <UrlRow label="Manifest" url={url()} onCopy={copy}>
              <button
                type="button"
                title="View manifest"
                aria-label="View manifest"
                onClick={() => {
                  setRequested(true);
                  setOpen(true);
                }}
                class="shrink-0 text-neutral-500 hover:text-brand-600 dark:hover:text-brand-400"
              >
                <Icon name="brackets-curly" />
              </button>
            </UrlRow>
            <Modal
              open={open()}
              onClose={() => setOpen(false)}
              size="xl"
              title={`Manifest · ${props.environment}`}
            >
              <Show when={!manifest.isPending} fallback={<Skeleton class="h-40" />}>
                <Show
                  when={!manifest.isError}
                  fallback={
                    <p class="text-sm text-red-600 dark:text-red-400">{manifest.error?.message}</p>
                  }
                >
                  <Show
                    when={manifest.data?.data.manifest}
                    fallback={
                      <p class="text-sm text-neutral-500">Nothing published at this URL yet</p>
                    }
                  >
                    {(m) => (
                      <pre class="max-h-[70vh] overflow-auto rounded-md bg-neutral-900 p-3 font-mono text-xs leading-relaxed text-neutral-100">
                        {JSON.stringify(m(), null, 2)}
                      </pre>
                    )}
                  </Show>
                </Show>
              </Show>
            </Modal>
          </div>
        )}
      </Show>
      <Show when={props.channel?.latestDownloadUrlBase}>
        {(base) => (
          <LatestDownloads
            base={base()}
            productId={props.productId}
            environment={props.environment}
            onCopy={copy}
          />
        )}
      </Show>
    </div>
  );
}

/**
 * Stable version-agnostic installer links (`…/latest/{env}/{packageId}/…`) —
 * the downloads CDN 302s each one to the newest published installer. One row
 * per required platform/arch, plus an MSI variant on windows.
 */
function LatestDownloads(props: {
  base: string;
  productId: string;
  environment: ReleaseEnvironment;
  onCopy: (value: string) => void;
}) {
  const required = createQuery(() => ({
    queryKey: ['required-artifacts', props.productId, props.environment] as const,
    queryFn: () =>
      apiCall(listRequiredArtifacts, {
        params: { productId: props.productId },
        query: { environment: props.environment },
      }),
    enabled: !!props.productId,
  }));

  const links = () =>
    (required.data?.data ?? []).flatMap(({ platform, arch }) => [
      { label: `${platform}/${arch}`, url: `${props.base}/${platform}/${arch}` },
      ...(platform === 'windows'
        ? [{ label: `${platform}/${arch} msi`, url: `${props.base}/${platform}/${arch}/msi` }]
        : []),
    ]);

  return (
    <div class="mt-3 border-t border-neutral-200 pt-2 dark:border-neutral-800">
      <p class="mb-1 text-xs font-medium text-neutral-500">Latest downloads</p>
      <Show when={!required.isPending} fallback={<Skeleton class="h-8" />}>
        <Show
          when={links().length > 0}
          fallback={<p class="text-xs text-neutral-500">No required artifacts configured</p>}
        >
          <ul class="space-y-1">
            <For each={links()}>
              {(link) => (
                <li>
                  <UrlRow label={link.label} url={link.url} onCopy={props.onCopy} />
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>
    </div>
  );
}

/** `label · URL (fade + marquee, pauses on hover) · copy · extra actions` on one line. */
function UrlRow(props: {
  label: string;
  url: string;
  onCopy: (value: string) => void;
  children?: JSX.Element;
}) {
  return (
    <div class="flex items-center gap-2">
      <span class="w-28 shrink-0 truncate text-xs text-neutral-500">{props.label}</span>
      <MarqueeText text={props.url} />
      <button
        type="button"
        title="Copy URL"
        aria-label={`Copy ${props.label} URL`}
        onClick={() => props.onCopy(props.url)}
        class="shrink-0 text-neutral-500 hover:text-brand-600 dark:hover:text-brand-400"
      >
        <Icon name="copy" />
      </button>
      {props.children}
    </div>
  );
}

/**
 * Single-line text that, when it overflows, keeps scrolling to its end and
 * back. Edge fades mark the hidden side: right at the start, both mid-scroll,
 * left at the end. Hovering pauses it in place. Static (right fade only) when
 * under prefers-reduced-motion; no fades when it fits.
 */
function MarqueeText(props: { text: string }) {
  let outer!: HTMLDivElement;
  let inner!: HTMLSpanElement;
  const [shift, setShift] = createSignal(0);
  const measure = () => setShift(Math.max(0, inner.scrollWidth - outer.clientWidth));

  onMount(() => {
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    ro.observe(inner);
    onCleanup(() => ro.disconnect());
  });

  return (
    <div
      ref={outer}
      title={props.text}
      class="marquee relative min-w-0 flex-1 overflow-hidden whitespace-nowrap"
      // Shared by the text and both fades so they run on one timeline;
      // ~40px/s across the 85% of the cycle that moves.
      style={`--marquee-shift: -${shift()}px; --marquee-duration: ${Math.max(1.5, shift() / 40 / 0.85)}s`}
    >
      <span
        ref={inner}
        class={cn(
          // pr-8 = fade width, so the scrolled-to end of the text clears the fade.
          'inline-block pr-8 font-mono text-xs text-neutral-500',
          shift() > 0 && 'animate-marquee',
        )}
      >
        {props.text}
      </span>
      <Show when={shift() > 0}>
        <div class="pointer-events-none absolute inset-y-0 left-0 w-8 animate-marquee-fade-start bg-gradient-to-r from-surface opacity-0 dark:from-surface-dark" />
        <div class="pointer-events-none absolute inset-y-0 right-0 w-8 animate-marquee-fade-end bg-gradient-to-l from-surface dark:from-surface-dark" />
      </Show>
    </div>
  );
}
